// Server-orchestrated board animations (boardFx). The server appends an fx
// manifest to MatchState whenever an automation needs a choreographed reveal
// (Howl from Beyond's deal is the first consumer); the record rides the normal
// diff and BOTH clients render the identical playback from this fold. The
// server is the choreographer. There is no caster-side relay, so the two
// boards can never disagree on what played out. State changes ride the normal
// zone folds; an fx entry is presentation only.
//
// Dedupe is by fx id (`host._seenBoardFx`). A Full (reconnect/resume/gap-heal)
// marks every record seen WITHOUT replaying. The restored zones already show
// the outcome, and replaying a stale reveal after a reload would be noise.

import { playSound, playLightningImpact } from '../../../utils/game/sounds';
import { computeGridPositions } from '../../../utils/game/grid';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { sampleCellGeometry } from '../../../utils/game/engine/core';
import { HOWL_DEAL_MS, HOWL_HOLD_MS, HOWL_RESOLVE_MS, howlDurationMs } from '../../../utils/game/engine/automation/ops/cards/howlFromBeyond';
import { applyHandAdditionsFromMirror } from './authZones';

function cellCenter(host, col, row) {
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return { x: 0, z: 0 };
  const { colPositions, rowPositions } = computeGridPositions(grid);
  if (col == null || row == null || col >= colPositions.length - 1 || row >= rowPositions.length - 1) return { x: 0, z: 0 };
  const corners = sampleCellGeometry(grid, col, row);
  if (!corners) return { x: 0, z: 0 };
  const { tl, tr, bl, br } = corners;
  return { x: (tl.x + tr.x + bl.x + br.x) / 4, z: (tl.z + tr.z + bl.z + br.z) / 4 };
}

export function applyBoardFxFromMirror(host, mirrorState, { isFull = false } = {}) {
  const fxList = mirrorState?.boardFx;
  if (!Array.isArray(fxList) || fxList.length === 0) return;
  const notices = [];
  const seen = host._seenBoardFx || (host._seenBoardFx = new Set());
  for (const fx of fxList) {
    if (!fx?.id || seen.has(fx.id)) continue;
    seen.add(fx.id);
    if (isFull) continue;
    if (fx.kind === 'howl_deal' || fx.kind === 'reveal') {
      notices.push(...buildRevealNotices(host, fx));
      playHowlFx(host, fx);
    }
    else if (fx.kind === 'lightning') playLightningFx(host, fx);
    else if (fx.kind === 'draw') playDrawFx(host, fx);
  }
  if (notices.length) host._pushStorylineEvents?.(notices);
}

// Keep an inspectable snapshot above the board until this viewer acknowledges it.
// The server already resolved the effect; dismissing this notice changes no zones.
function buildRevealNotices(host, fx) {
  if (host.props?.replayDriver || host.props?.isSpectating) return [];
  const controller = host._viewerPlayer?.();
  return (fx.steps || []).map((step, index) => {
    const full = host._getFullCard?.(step.cardId);
    const cardName = full?.name || 'Card';
    return {
      id: `ack-reveal-${fx.id}-${index}`,
      promptType: 'acknowledge',
      revealNotice: true,
      controller,
      cardName,
      description: `${cardName} was revealed.`,
      cardInstance: {
        id: step.instanceId,
        cardId: step.cardId,
        name: cardName,
        imageUrl: full?.printings?.[0]?.image_url || full?.imageUrl || null,
        isSite: full?.type === 'Site',
      },
    };
  });
}

// Server draw manifest. The explicit "a draw happened" event. The hands[]
// diff stays authoritative for state; this record exists so the fly-to-hand
// animation triggers off an explicit signal instead of being inferred from a
// hand-array diff: drop the drawn ids from any stale pile render (the server's
// pile shrank the moment it drew) and run the hand-additions fold, which
// schedules the staggered fly-in + draw sound per card. Opponent draws online
// are a no-op here (their deck renders as placeholder stacks and the hand fold
// only covers piloted seats).
function playDrawFx(host, fx) {
  const ids = Array.isArray(fx.cardIds) ? fx.cardIds : [];
  if (ids.length === 0) return;
  const gs = host.state?.gameState;
  let dropped = false;
  for (const p of gs?.piles || []) {
    if (!Array.isArray(p.cards)) continue;
    const kept = p.cards.filter((c) => !ids.includes(c.id));
    if (kept.length !== p.cards.length) { p.cards = kept; dropped = true; }
  }
  if (dropped) {
    host.updatePileMeshes?.();
    host.forceUpdate?.();
  }
  applyHandAdditionsFromMirror(host, host._authMirror?.getState?.());
}

// Sparkmage shock bolt. The server strike is the only place that knows where
// the (seeded-random) hit landed, so the bolt rides the manifest. Anchors on
// the struck mesh when it still exists (live height), else the manifest coords.
function playLightningFx(host, fx) {
  if (!host.lightningLayer) return;
  try {
    const mesh = fx.targetId ? host.meshes?.get(fx.targetId) : null;
    const p = mesh?.position || { x: fx.x ?? 0, y: 0.5, z: fx.z ?? 0 };
    // The id also seeds cosmetic variation identically on both clients.
    const seed = [...String(fx.id)].reduce((n, c) => (Math.imul(n, 31) + c.charCodeAt(0)) | 0, 0);
    host.lightningLayer.fire({ x: p.x, y: p.y ?? 0.5, z: p.z, variant: fx.variant || 'bolt', seed,
      onImpact: ({ variant, position }) => {
        const pan = host.scene?.camera ? position.clone().project(host.scene.camera).x : 0;
        playLightningImpact({ variant, pan });
      },
    });
  } catch (err) {
    console.warn('[boardFx] lightning playback failed (cosmetic, ignored)', err);
  }
}

// Build the reveal record `_playHowlScene` renders from the server manifest:
// the manifest carries the authoritative WHAT (dealt ids + cells +
// classifications); the client derives the local HOW (world coords, card art,
// fly-in origin at the caster's spellbook).
function playHowlFx(host, fx) {
  const spellbook = host.findPileByNameForPlayer?.('Spellbook', fx.seat === 'p2' ? 2 : 1);
  const rec = {
    howlId: fx.id,
    seat: fx.seat,
    originX: spellbook?.x ?? 0,
    originZ: spellbook?.z ?? 0,
    steps: (fx.steps || []).map((s) => {
      const full = host._getFullCard?.(s.cardId);
      const center = cellCenter(host, s.col, s.row);
      return {
        cardId: s.cardId,
        name: full?.name || 'Card',
        // Art lives on the PRINTING, not the catalog row itself. A bare
        // catalog read renders the card back (the "all face down" reveal).
        imageUrl: full?.printings?.[0]?.image_url || full?.imageUrl || null,
        x: center.x,
        z: center.z,
        classification: s.classification,
        accent: s.classification === 'banish' ? 'warning' : 'harbinger',
      };
    }),
  };
  host._playHowlScene?.(rec);

  // Sync the STATE render to the choreography: the diff resolves the whole
  // deal instantly, so without a hold the keepers pop into the hand seconds
  // before the procession delivers them. Hold each keeper out of the
  // hand-additions fold until its resolve beat, then release + re-fold so the
  // card lands in the hand as its reveal mesh flies off. A reload mid-reveal
  // loses the holds. The next fold adds everything immediately (state wins).
  if (fx.kind === 'howl_deal' && (host.props?.isCasualSandbox || fx.seat === (host.state?.isHost ? 'p1' : 'p2'))) {
    const holds = host._howlHoldIds || (host._howlHoldIds = new Set());
    const keepers = (fx.steps || []).filter((s) => s.classification === 'draw');
    keepers.forEach((s) => holds.add(s.instanceId));
    const n = (fx.steps || []).length;
    const resolveStart = HOWL_DEAL_MS * n + HOWL_HOLD_MS;
    const release = (ids) => {
      if (host._unmounted) return;
      ids.forEach((id) => holds.delete(id));
      applyHandAdditionsFromMirror(host, host._authMirror?.getState?.());
    };
    (fx.steps || []).forEach((s, i) => {
      if (s.classification !== 'draw') return;
      setTimeout(() => release([s.instanceId]), resolveStart + HOWL_RESOLVE_MS * i + 400);
    });
    // Safety sweep: whatever a dropped timer leaves behind is released at
    // teardown so a hold can never block future hand folds.
    setTimeout(() => release(keepers.map((s) => s.instanceId)), howlDurationMs(n) + 1000);
  }
}
